import { useState, useRef, useEffect } from 'react';
import type { CombatLayoutType } from '../../utils/combatLayoutDetector';
import type { CombatLayoutPreference } from '../../hooks/useCombatLayout';

interface CombatLayoutToggleProps {
  preference: CombatLayoutPreference;
  autoDetectedLayout: CombatLayoutType;
  onPreferenceChange: (preference: CombatLayoutPreference) => void;
}

export function CombatLayoutToggle({
  preference,
  autoDetectedLayout,
  onPreferenceChange,
}: CombatLayoutToggleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const layoutLabels: Record<CombatLayoutPreference, string> = {
    'auto': 'Auto',
    'combat-priority': 'Combat',
    'tab-within-tab': 'Tabs',
  };

  const layoutIcons: Record<CombatLayoutPreference, string> = {
    'auto': '⚙',
    'combat-priority': '⚔',
    'tab-within-tab': '📑',
  };

  const autoDetectedLabel = autoDetectedLayout === 'combat-priority' ? 'Combat' : 'Tabs';
  const currentLabel = preference === 'auto' ? `Auto (${autoDetectedLabel})` : layoutLabels[preference];
  const currentIcon = layoutIcons[preference];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (pref: CombatLayoutPreference) => {
    onPreferenceChange(pref);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Compact Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border border-background-elevated bg-background-card text-text-muted hover:text-text-primary hover:border-text-muted transition-all"
        aria-label="Combat layout settings"
        aria-expanded={isOpen}
      >
        <span className="text-sm">{currentIcon}</span>
        <span className="font-display">{currentLabel}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 mt-1 w-48 bg-background-card border border-background-elevated rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="py-1">
            <button
              onClick={() => handleSelect('auto')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-xs text-left transition-colors ${
                preference === 'auto'
                  ? 'bg-accent-gold/20 text-text-primary'
                  : 'text-text-muted hover:bg-background-elevated hover:text-text-primary'
              }`}
            >
              <span className="text-base">{layoutIcons['auto']}</span>
              <div className="flex-1">
                <div className="font-display">{layoutLabels['auto']}</div>
                <div className="text-[10px] opacity-75">Adaptive: {autoDetectedLabel}</div>
              </div>
              {preference === 'auto' && (
                <span className="text-accent-gold">✓</span>
              )}
            </button>

            <button
              onClick={() => handleSelect('combat-priority')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-xs text-left transition-colors ${
                preference === 'combat-priority'
                  ? 'bg-accent-bronze/20 text-text-primary'
                  : 'text-text-muted hover:bg-background-elevated hover:text-text-primary'
              }`}
            >
              <span className="text-base">{layoutIcons['combat-priority']}</span>
              <div className="flex-1">
                <div className="font-display">{layoutLabels['combat-priority']}</div>
                <div className="text-[10px] opacity-75">Combat Priority</div>
              </div>
              {preference === 'combat-priority' && (
                <span className="text-accent-bronze">✓</span>
              )}
            </button>

            <button
              onClick={() => handleSelect('tab-within-tab')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-xs text-left transition-colors ${
                preference === 'tab-within-tab'
                  ? 'bg-class-wizard/20 text-text-primary'
                  : 'text-text-muted hover:bg-background-elevated hover:text-text-primary'
              }`}
            >
              <span className="text-base">{layoutIcons['tab-within-tab']}</span>
              <div className="flex-1">
                <div className="font-display">{layoutLabels['tab-within-tab']}</div>
                <div className="text-[10px] opacity-75">Tab Layout</div>
              </div>
              {preference === 'tab-within-tab' && (
                <span className="text-class-wizard">✓</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
